/**
 * initialState.ts — Creates the seed DemoState from existing mock data.
 *
 * This is called ONLY on first load (no persisted state exists).
 * After the first load, state is read from localStorage and migrated.
 */

import type { DemoState, DemoWorkspace, DemoIssue, DemoNotification } from './schema';
import { CURRENT_VERSION, CURRENT_SCHEMA_VERSION } from './schema';
import {
  mockPlendarySessions,
  mockAgendaItems,
  mockDocuments,
  mockDrafts,
  mockIssues,
  mockSuggestedQuestions,
  mockActivityEvents,
  mockAlerts,
} from '@/data/mockData';
import { organizationMunicipalityConfigs } from '@/data/organizationData';

// ─── Default user profile ──────────────────────────────────────────────

const DEFAULT_USER_ID = 'user-demo-001';

// ─── Default workspaces ────────────────────────────────────────────────

const DEFAULT_WORKSPACES: DemoWorkspace[] = [
  {
    id: 'ws-muni-001-ac',
    municipalityId: 'muni-001',
    organizationId: 'org-alianca-catalana',
    nom: 'Berga',
    rolUsuari: 'Regidora',
    actiu: true,
    creatA: '2026-01-01',
  },
  // NOTE: ws-muni-001-general is NOT seeded — Berga General must behave
  // like any other municipality in PlenariCAT general (show "Crea espai de demostració").
  // Legacy ws-muni-001-general is removed by the schemaV3 migration.
  {
    id: 'ws-muni-002',
    municipalityId: 'muni-002',
    organizationId: 'org-alianca-catalana',
    nom: 'Manresa',
    rolUsuari: 'Regidor',
    actiu: true,
    creatA: '2026-01-01',
  },
  {
    id: 'ws-muni-003',
    municipalityId: 'muni-003',
    organizationId: 'org-alianca-catalana',
    nom: 'Vic',
    rolUsuari: 'Regidora',
    actiu: true,
    creatA: '2026-01-01',
  },
];

// ─── Seed issues with lifecycle fields ────────────────────────────────

function seedIssues(): DemoIssue[] {
  return mockIssues.map((issue) => {
    const demoIssue: DemoIssue = {
      ...issue,
      municipalityId: issue.municipiId,
      workspaceId: issue.workspaceId ?? null,
      archivedAt: issue.archivedAt ?? null,
      deletedAt: issue.deletedAt ?? null,
      deletedBy: issue.deletedBy ?? null,
      createdBy: issue.createdBy ?? null,
      updatedBy: issue.updatedBy ?? null,
      archivedBy: issue.archivedBy ?? null,
      statusBeforeArchive: null,
    };

    // Normalise legacy: status === 'arxivat' but no archivedAt
    if (issue.status === 'arxivat' && !issue.archivedAt) {
      demoIssue.archivedAt = new Date().toISOString();
      demoIssue.statusBeforeArchive = 'en_estudi';
    }

    return demoIssue;
  });
}

// ─── Workspace mapping for notification seed ───────────────────────────

const ALERT_WORKSPACE_MAP: Record<string, string> = {
  'muni-001': 'ws-muni-001-general',
  'muni-002': 'ws-muni-002',
  'muni-003': 'ws-muni-003',
};

// ─── Seed notifications from alerts ───────────────────────────────────

function seedNotifications(): DemoNotification[] {
  return mockAlerts.map((alert) => ({
    id: alert.id,
    municipalityId: alert.municipiId,
    workspaceId: ALERT_WORKSPACE_MAP[alert.municipiId] ?? null,
    entityId: alert.entitatId ?? null,
    entityType: alert.entitatTipus ?? null,
    titol: alert.titol,
    descripcio: alert.descripcio,
    data: alert.data,
    llegit: alert.llegit,
    tipus: alert.tipus,
    prioritat: alert.prioritat,
  }));
}

// ─── Seed OrganizationMunicipalityConfigs from organizationData ────────

function seedOrgMunicipalityConfigs(): import('./schema').DemoOrgMunicipalityConfig[] {
  return organizationMunicipalityConfigs.map((c) => ({
    id: c.id,
    organizationId: c.organizationId,
    municipalityId: c.municipalityId,
    organizationEnabled: c.organizationEnabled,
    workspaceStatus: c.workspaceStatus,
    politicalStatus: c.politicalStatus,
    workspaceId: c.workspaceId ?? null,
    estructuraLocal: c.estructuraLocal,
    dadesDemoDisponibles: c.dadesDemoDisponibles,
    visibleAlDirectoriIntern: c.visibleAlDirectoriIntern,
    dataActualitzacio: c.dataActualitzacio ?? null,
    fontInterna: c.fontInterna ?? null,
    notesInternes: c.notesInternes ?? null,
  }));
}

// ─── createInitialDemoState ────────────────────────────────────────────

export function createInitialDemoState(): DemoState {
  const now = new Date().toISOString();

  return {
    version: CURRENT_VERSION,
    schemaVersion: CURRENT_SCHEMA_VERSION,

    userProfiles: [
      {
        id: DEFAULT_USER_ID,
        nom: 'Marta',
        cognoms: 'Serra',
        email: 'demo@plenari.cat',
        telefon: null,
        avatarInicials: 'MS',
      },
    ],

    organizationMemberships: [
      {
        id: 'omem-001',
        userId: DEFAULT_USER_ID,
        organizationId: 'org-alianca-catalana',
        activa: true,
        roles: ['vocal_2'],
        customRoleLabel: null,
      },
    ],

    workspaceMemberships: [
      {
        id: 'wmem-001',
        userId: DEFAULT_USER_ID,
        workspaceId: 'ws-muni-001-ac',
        institutionalRoles: ['regidor'],
        active: true,
      },
      // wmem-001b (ws-muni-001-general) removed — Berga General is not pre-seeded
      {
        id: 'wmem-002',
        userId: DEFAULT_USER_ID,
        workspaceId: 'ws-muni-002',
        institutionalRoles: ['regidor'],
        active: true,
      },
      {
        id: 'wmem-003',
        userId: DEFAULT_USER_ID,
        workspaceId: 'ws-muni-003',
        institutionalRoles: ['regidor'],
        active: true,
      },
    ],

    workspaces: DEFAULT_WORKSPACES,
    activeWorkspaceId: null,
    activeOrganizationId: null,

    // Seed from organizationData — persisted to localStorage
    organizationMunicipalityConfigs: seedOrgMunicipalityConfigs(),

    plenarySessions: mockPlendarySessions.map((s) => ({
      ...s,
      municipalityId: s.municipiId,
      workspaceId: s.workspaceId ?? null,
    })),

    agendaItems: mockAgendaItems.map((item) => ({
      ...item,
      workspaceId: item.workspaceId ?? null,
    })),

    documents: mockDocuments.map((doc) => ({
      ...doc,
      workspaceId: doc.workspaceId ?? null,
    })),

    citations: [],

    issues: seedIssues(),

    drafts: mockDrafts.map((d) => ({
      ...d,
      municipalityId: d.municipiId,
      workspaceId: d.workspaceId ?? null,
      archivedAt: d.archivedAt ?? null,
      deletedAt: d.deletedAt ?? null,
      deletedBy: d.deletedBy ?? null,
      presentedAt: d.presentedAt ?? (d.status === 'presentat' ? now : null),
      respondedAt: d.respondedAt ?? (d.status === 'respost' ? now : null),
      createdBy: d.createdBy ?? null,
      updatedBy: d.updatedBy ?? null,
      archivedBy: d.archivedBy ?? null,
    })),

    suggestedQuestions: mockSuggestedQuestions.map((q) => ({
      ...q,
      workspaceId: q.workspaceId ?? null,
      dismissedAt: q.dismissedAt ?? null,
    })),

    internalNotes: {},
    itemResponsables: {},

    activityEvents: mockActivityEvents.map((a) => ({
      ...a,
      municipalityId: a.municipiId,
      workspaceId: a.workspaceId ?? null,
    })),

    alerts: seedNotifications(),

    notifications: seedNotifications(),

    preferences: {
      theme: 'clar',
      notifNouDocument: true,
      notifTerminis: true,
      notifEsborranys: false,
      notifAssumptes: true,
    },

    metadata: {
      createdAt: now,
      lastModifiedAt: now,
      seedVersion: '2.0.0',
    },
  };
}
